import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';

const MetricsCard = ({ title, value, subtitle, icon, trend, trendValue, color = 'primary', metricType }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isUpdating, setIsUpdating] = useState(false);
  const { userProfile } = useAuth();

  // Set up real-time subscriptions for metrics
  useEffect(() => {
    if (!userProfile?.id) return;
    
    let channel;
    
    // Set up different subscriptions based on metric type
    if (metricType === 'profile_views') {
      // Initialize with the provided value
      setLocalValue(value);
      
      // Subscribe to changes in profile_views table (new views added)
      channel = supabase
        .channel('profile_views_changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'profile_views',
            filter: `profile_id=eq.${userProfile.id}`
          },
          (payload) => {
            // Show brief loading indicator
            setIsUpdating(true);
            
            // Calculate new weekly views count
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
            
            supabase
              .from('profile_views')
              .select('id')
              .eq('profile_id', userProfile.id)
              .gte('viewed_at', oneWeekAgo.toISOString())
              .then(({ data, error }) => {
                if (!error && data) {
                  setLocalValue(data.length);
                }
                // Remove loading indicator after a short delay
                setTimeout(() => setIsUpdating(false), 500);
              });
          }
        )
        .subscribe();
    } else if (metricType === 'match_score') {
      // Initialize with the provided value
      setLocalValue(value);
      
      // Subscribe to changes in jobs table (new jobs published) and job_matches table
      channel = supabase
        .channel('match_score_changes')
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'jobs'
          },
          async (payload) => {
            // Only update if a job was published/updated
            if (payload.new?.status === 'published' || payload.eventType === 'INSERT') {
              setIsUpdating(true);
              
              try {
                // Recalculate match score with fresh data
                const { getUserDashboardMetrics } = await import('../../../services/dashboardService.js');
                const metrics = await getUserDashboardMetrics(userProfile.id);
                const matchMetric = metrics.find(m => m.title === 'Match Score');
                
                if (matchMetric) {
                  // Extract percentage from value like "75%"
                  const newValue = matchMetric.value.replace('%', '');
                  setLocalValue(newValue);
                }
              } catch (error) {
                console.error('Error updating match score:', error);
              }
              
              setTimeout(() => setIsUpdating(false), 1000);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'job_matches',
            filter: `candidate_id=eq.${userProfile.id}`
          },
          (payload) => {
            // Update when job matches are created/updated for this user
            setIsUpdating(true);
            
            supabase
              .from('job_matches')
              .select('match_score')
              .eq('candidate_id', userProfile.id)
              .order('created_at', { ascending: false })
              .limit(10)
              .then(({ data, error }) => {
                if (!error && data && data.length > 0) {
                  const totalScore = data.reduce((sum, match) => sum + match.match_score, 0);
                  const avgScore = Math.round(totalScore / data.length);
                  setLocalValue(avgScore);
                }
                setTimeout(() => setIsUpdating(false), 500);
              });
          }
        )
        .subscribe();
    } else if (metricType === 'response_rate') {
      // Initialize with the provided value
      setLocalValue(value);
      
      // Subscribe to changes in messages table (new messages sent/received).
      // Use a unique channel name per user to avoid conflicts with other components
      // or multiple MetricsCard instances on the same page.
      channel = supabase
        .channel(`response_rate_changes_${userProfile.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          async (payload) => {
            // Update when new messages are sent by or to this user
            if (payload.new?.sender_id === userProfile.id || payload.new?.recipient_id === userProfile.id) {
              setIsUpdating(true);
              
              try {
                // Recalculate response rate with fresh data
                const { getUserDashboardMetrics } = await import('../../../services/dashboardService.js');
                const metrics = await getUserDashboardMetrics(userProfile.id);
                const responseMetric = metrics.find(m => m.title === 'Response Rate');
                
                if (responseMetric) {
                  // Extract percentage from value like "85%"
                  const newValue = responseMetric.value.replace('%', '');
                  setLocalValue(newValue);
                }
              } catch (error) {
                console.error('Error updating response rate:', error);
              }
              
              setTimeout(() => setIsUpdating(false), 1000);
            }
          }
        )
        .subscribe();
    } else {
      // For other metrics, just use the provided value
      setLocalValue(value);
    }
    
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [userProfile?.id, metricType, value]);

  // Format value for display
  const formattedValue = () => {
    let displayValue = localValue;
    
    // Use local value for metrics with real-time updates
    if (metricType === 'profile_views' || metricType === 'match_score' || metricType === 'response_rate') {
      displayValue = localValue;
    } else {
      displayValue = value;
    }
    
    // Format percentage metrics with percentage sign
    if (metricType === 'match_score' || metricType === 'response_rate') {
      // If displayValue is a string like "75%", extract the number
      const numValue = typeof displayValue === 'string' ? 
        parseInt(displayValue.replace('%', '')) : displayValue;
      return `${numValue}%`;
    }
    
    // Format large numbers with commas
    if (typeof displayValue === 'number' && displayValue > 999) {
      return displayValue.toLocaleString();
    }
    
    return displayValue;
  };

  const getColorClasses = () => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-success-50',
          icon: 'text-success',
          text: 'text-success-700'
        };
      case 'warning':
        return {
          bg: 'bg-warning-50',
          icon: 'text-warning',
          text: 'text-warning-700'
        };
      case 'accent':
        return {
          bg: 'bg-accent-50',
          icon: 'text-accent',
          text: 'text-accent-700'
        };
      default:
        return {
          bg: 'bg-primary-50',
          icon: 'text-primary',
          text: 'text-primary-700'
        };
    }
  };

  const colorClasses = getColorClasses();

  return (
    <div className="card p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-text-secondary mb-1">{title}</p>
          <div className="flex items-center">
            <p className="text-2xl font-bold text-text-primary">
              {isUpdating ? (
                <span className="inline-block w-6 h-6 rounded-full border-2 border-t-transparent border-primary animate-spin mr-2"></span>
              ) : null}
              {formattedValue()}
            </p>
            {(metricType === 'profile_views' || metricType === 'match_score' || metricType === 'response_rate') && isUpdating && (
              <span className="ml-2 text-xs text-success animate-pulse">updating...</span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-text-muted mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg ${colorClasses.bg} flex items-center justify-center`}>
          <Icon name={icon} size={24} className={colorClasses.icon} />
        </div>
      </div>
      
      {trend && trendValue && (
        <div className="flex items-center mt-3 pt-3 border-t border-border">
          {trend === 'neutral' ? (
            <Icon name="MinusCircle" size={16} className="text-text-secondary" />
          ) : (
            <Icon 
              name={trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
              size={16} 
              className={trend === 'up' ? 'text-success' : 'text-error'} 
            />
          )}
          <span className={`text-sm font-medium ml-1 ${
            trend === 'up' ? 'text-success' : 
            trend === 'down' ? 'text-error' : 
            'text-text-secondary'
          }`}>
            {trendValue}
          </span>
          <span className="text-sm text-text-muted ml-1">vs last period</span>
        </div>
      )}
    </div>
  );
};

export default MetricsCard;
